/**
 * Maps a raw NHTSA vPIC `decodevin` response into the normalized
 * {@link VinLookupResponse} shape the modal form consumes.
 *
 * The flat `decodevin/{vin}?format=json` endpoint returns:
 *   { Results: [ { Variable: "Make", Value: "ACURA", ValueId, VariableId }, ... ] }
 * Many entries come back with Value === null or "Not Applicable" / "0".
 *
 * @module vehicleModal/utils/vinMapping
 */

/**
 * NHTSA `Variable` label -> our normalized field name.
 * Only the fields we surface in the form are mapped.
 */
const NHTSA_FIELD_MAP = {
  "Model Year": "year",
  Make: "make",
  Model: "model",
  Trim: "trim",
  Series: "trim", // fallback when Trim is empty
  "Body Class": "bodyStyle",
  "Fuel Type - Primary": "fuelType",
  "Transmission Style": "transmission",
  "Drive Type": "driveType",
  "Vehicle Type": "vehicleType",
  "Engine Number of Cylinders": "engineCylinders",
  Doors: "doors",
  Seats: "seats",
  "Number of Seats": "seats",
};

/** Values NHTSA returns that should be treated as "no data". */
const EMPTY_VALUES = new Set(["", "0", "not applicable", "n/a"]);

/**
 * @param {*} raw - a single NHTSA Value.
 * @returns {string|undefined} trimmed value, or undefined when empty/placeholder.
 */
function cleanValue(raw) {
  if (raw === null || raw === undefined) return undefined;
  const str = String(raw).trim();
  if (!str || EMPTY_VALUES.has(str.toLowerCase())) return undefined;
  return str;
}

/**
 * NHTSA returns ALL-CAPS makes; convert to Title Case so they match the
 * casing used by the Make/Model dropdowns and look right on cards.
 * @param {string} value
 * @returns {string}
 */
function titleCase(value) {
  return value
    .toLowerCase()
    .split(/\s+/)
    .map(word => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/**
 * Normalize a NHTSA decodevin response into {@link VinLookupResponse}.
 * Title-cases Make (NHTSA returns "ACURA") and leaves a sensible Make/Model
 * casing for display.
 *
 * @param {{ Results?: Array<{Variable: string, Value: *}> }} response
 * @returns {import("../types").VinLookupResponse}
 */
export function mapVinResponse(response) {
  const results = response && Array.isArray(response.Results) ? response.Results : [];
  /** @type {import("../types").VinLookupResponse} */
  const mapped = {};

  results.forEach(item => {
    if (!item || !item.Variable) return;
    const field = NHTSA_FIELD_MAP[item.Variable];
    if (!field) return;
    const value = cleanValue(item.Value);
    if (value === undefined) return;
    // Don't let a later "Series" overwrite an earlier real "Trim".
    if (field === "trim" && mapped.trim) return;
    mapped[field] = value;
  });

  if (mapped.make) mapped.make = titleCase(mapped.make);

  return mapped;
}

/**
 * A decode is considered "found" only if NHTSA returned at least a Make or
 * Model (a structurally valid VIN with no match still returns Results, but
 * with everything null).
 * @param {import("../types").VinLookupResponse} mapped
 * @returns {boolean}
 */
export function hasVehicleData(mapped) {
  return Boolean(mapped && (mapped.make || mapped.model || mapped.year));
}
